import { Component, ViewChild, ViewChildren, QueryList, ElementRef, OnDestroy, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { Chart} from 'src/app/utils/chart-config';
import { VibrationLineChart } from '../../bo/vibration';
import { HttpService } from '../../services/http.service';
import { Graphique } from '../../bo/graphique';
import { forkJoin } from 'rxjs';
import { UtilisateurService } from '../../services/utilisateur.service';
import { KeycloakUtilisateurService } from 'src/app/services/keycloak/keycloak-utilisateur.service';

export const VibrationGraphColors = [
  '#6994D5', '#FF8033', '#E53C68', '#4CC7BA', '#006D99', "#EB0080", '#A6038B', '#F27329', '#F25244', '#0D0D0D'
];

export const VibrationChartOption = {
  maintainAspectRatio: false,
  responsive: true,
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: { usePointStyle: true }
    }
  },
  elements: {
    point: {
      radius: 3
    },
    line: {
      tension: 0
    }
  },
  scales: {
    y: {
      id: "axeMms",
      display: true,
      title: {
        display: true,
        text: 'mm/s',
        font: {
          size: 14
        }
      },
      position: "left" as const,
      ticks: {
        beginAtZero: false,
        color: '#7E003F'
      }
    },
    x: {
      id: "axeX",
      type: 'time' as const,
      display: true,
      position: "bottom" as const,
      time: {
        unit: 'day' as const,
        round: 'day' as const,
        displayFormats: {
          day: 'MMM D'
        }
      }
    }
  }
};

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false
})
export class DashboardComponent implements  OnDestroy {
  @ViewChild('modalConfirmSuppr') modalConfirmSuppr: ModalDirective;
  @ViewChildren('chartCanvas') chartCanvases: QueryList<ElementRef<HTMLCanvasElement>>;

  chartParametres = signal<string[]>([]); 
  indexToDelete: number;
  erreurRecuperation = signal(false);
  graphiques = signal<Graphique[]>([]);
  chartData = signal<any[]>([]);
  chartOptions = signal<any[]>([]);
  charts = signal<any[]>([]);
  dataLoading = signal(false);
  dashboardVide = signal(false);
  selectedDashboard: string;
  loadMetabase = signal(true);
  
  private chartInstances: any[] = [];
  private isCreatingCharts = false;

  constructor(
    private readonly http: HttpService,
    private readonly translate: TranslateService,
    private readonly utilService: UtilisateurService,
    private readonly route: ActivatedRoute,
    private readonly keycloakUtilisateurService: KeycloakUtilisateurService
    ) { }

  ngOnInit() {
    this.selectedDashboard = "metabase";

    this.route.params.subscribe(() => {
      this.graphiques.set([]);
      this.chartData.set([]);
      this.chartOptions.set([]);
      this.charts.set([]);
      this.refresh();
    });
  }

  showConfirmSuppr(index: number) {
    this.indexToDelete = index;
    this.modalConfirmSuppr.show();
  }

  hideConfirmSuppr() {
    this.modalConfirmSuppr.hide();
  }

  refresh() {
    this.reloadMetabase();
    this.dataLoading.set(true);
    this.dashboardVide.set(false);
    
    this.destroyCharts();
    this.charts.set([]);
    this.chartData.set([]);
    this.chartOptions.set([]);
    
    this.http.get("Users/" + this.keycloakUtilisateurService.userProfile.id + "/Sites/" + this.utilService.currentSite().siteId + "/Charts").subscribe((res: Graphique[]) => {
      this.graphiques.set(res);
      
      if (this.graphiques().length == 0) this.dashboardVide.set(true);
 
      let rqs = [];

      this.graphiques().forEach((g:Graphique) => {

        let chartParametre = '';

        if (g.type === "LineChart") {

          chartParametre = "LineChart"
          + "?siteId=" + g.siteId

          chartParametre += this.split(g.parametre, 'dMin='); 

          chartParametre +=  this.split(g.parametre, 'dMax=');

          chartParametre +=  this.split(g.parametre, 'periode='); 

          chartParametre +=  this.split(g.parametre, 'typeTirId=', g.parametre.split('typeTirId').length, true); 

          chartParametre += this.split(g.parametre, 'echelleX='); 
         
          chartParametre +=  this.split(g.parametre, 'refDonneeId=',g.parametre.split('refDonneeId').length, true); 

          chartParametre +=  this.split(g.parametre, 'pbId=', g.parametre.split('pbId').length, true); 

          chartParametre +=  this.split(g.parametre, 'zone='); 

        } else if (g.type === "CombinedChart") {

          chartParametre = "CombinedChart"
          + "?siteId=" + g.siteId

          chartParametre +=  this.split(g.parametre, 'refDonneeId='); 

          chartParametre +=  this.split(g.parametre, 'dMin='); 

          chartParametre +=  this.split(g.parametre, 'dMax='); 

          chartParametre +=  this.split(g.parametre, 'periode='); 

        } else if (g.type === "BoxPlotChart"){

          chartParametre = "BoxPlotChart"
          + "?siteId=" + g.siteId

          chartParametre += this.split(g.parametre, 'dMin='); 

          chartParametre += this.split(g.parametre, 'dMax='); 

          chartParametre += this.split(g.parametre, 'typeTirId=', g.parametre.split('refDonneeId').length, true); 

          chartParametre += this.split(g.parametre, 'echelleX='); 

          chartParametre += this.split(g.parametre, 'refDonneeId='); 

          chartParametre += this.split(g.parametre, 'zone='); 

          chartParametre += this.split(g.parametre, 'periode='); 
        }
        else if (g.type === "Vibration"){

          chartParametre = "Vibrations"
          + "?siteId=" + g.siteId

          chartParametre += this.split(g.parametre, 'controlPointIdList=', g.parametre.split('controlPointIdList').length);

          chartParametre += this.split(g.parametre, 'dMin='); 

          chartParametre += this.split(g.parametre, 'dMax=');

          chartParametre += this.split(g.parametre, 'periode=');
        }

        rqs.push(this.http.get("Chart/" + chartParametre));
      });

      if (rqs.length == 0) { 
        this.dataLoading.set(false);
        this.createCharts();
      }

      forkJoin(rqs).subscribe(async (tabGraphData: any) => {
        const updatedChartData = [...this.chartData()];
        const updatedChartOptions = [...this.chartOptions()];
        const updatedCharts = [...this.charts()];
        
        for (var i = 0; i < tabGraphData.length; i++) {
          var max: number[] = [];
          var min: number[] = [];
          var nominal: number[] = [];
          var selections = (this.graphiques()[i].configurationSaisieDonneesAnalyses as Array<any>).concat(this.graphiques()[i].problemes);
          if (this.graphiques()[i].type == "CombinedChart") {
            updatedChartData[i] = tabGraphData[i];
            updatedCharts.push({ data: updatedChartData[i], options: null, type: "combinedChart" });
          }
          else if (this.graphiques()[i].type == "LineChart") {
            updatedChartOptions[i] = {
              maintainAspectRatio: false,
              responsive: true,
              legend: {
                position: 'bottom',
                labels: { usePointStyle: true }
              },
              elements: {
                point: {
                  radius: 0
                }
              },
              scales: {
                yAxes: [{
                  id: "axe1",
                  position: "left",
                  ticks: {
                    beginAtZero: true,
                    fontColor: '#7E003F'
                  }
                }]
              }
            };
            updatedChartOptions[i].scales.yAxes.push({ id: "axe1", position: "left", ticks: { beginAtZero: true, fontColor: "#7E003F", fontStyle: "bold", callback: function (value) { if (value % 1 === 0) { return value; } } } });

            if (tabGraphData[i].length > 0) {
              if (this.graphiques()[i].configurationSaisieDonneesAnalyses[0]) {
                var _label: string;
                var trad = await this.TraduitNom(this.graphiques()[i].configurationSaisieDonneesAnalyses[0].referentielDonneeAnalyse.nom);

                if (this.graphiques()[i].configurationSaisieDonneesAnalyses[0].referentielDonneeAnalyse.nomUniteReferentiel) {
                  _label = trad + " (" + this.graphiques()[i].configurationSaisieDonneesAnalyses[0].referentielDonneeAnalyse.nomUniteReferentiel + ")";
                }
                else {
                  _label = trad;
                }
              }
              else {
                // _label = this.graphiques()[i].problemes[0].nom;

                _label = '';

              }
              updatedChartData[i] = {
                // define label tree
                labels: tabGraphData[i][0] ? tabGraphData[i][0].x : [],
                datasets: [
                  {
                    lineTension: 0,
                    pointHitRadius: 2,
                    pointBackgroundColor: "#7E003F",
                    label: _label,
                    backgroundColor: "transparent",
                    borderColor: "#7E003F",
                    borderWidth: 2,
                    data: tabGraphData[i][0] ? tabGraphData[i][0].y : [],
                    type: 'line'
                  }
                ]
              };
            }

            if (tabGraphData[i].length == 2) {
              if (selections[1].referentielDonneeAnalyse) {
                var trad = await this.TraduitNom(this.graphiques()[i].configurationSaisieDonneesAnalyses[1].referentielDonneeAnalyse.nom);
                var _label = trad + " (" + this.graphiques()[i].configurationSaisieDonneesAnalyses[1].referentielDonneeAnalyse.nomUniteReferentiel + ")";
              } else {
                var _label = String(selections[1].nom);
              }
              updatedChartOptions[i].scales.yAxes.push({ id: "axe2", position: "right", ticks: { beginAtZero: true, fontColor: "#2B729B", fontStyle: "bold", callback: function (value) { if (value % 1 === 0) { return value; } } } });
              updatedChartData[i].datasets.push(
                {
                  lineTension: 0,
                  pointHitRadius: 2,
                  pointBackgroundColor: "#2B729B",
                  label: _label,
                  backgroundColor: "transparent",
                  borderColor: "#2B729B",
                  borderWidth: 2,
                  yAxisID: "axe2",
                  data: tabGraphData[i][1] ? tabGraphData[i][1].y : [],
                  type: 'line'
                }
              );
            }

            //Ajout des courbes min, max, nominale si une seule data selectionnée
            if (tabGraphData[i].length == 1) {
              for (let j = 0; j < tabGraphData[i][0].x.length; j++) {
                if (this.graphiques()[i].configurationSaisieDonneesAnalyses[0].valeurMax) max.push(this.graphiques()[i].configurationSaisieDonneesAnalyses[0].valeurMax);
                if (this.graphiques()[i].configurationSaisieDonneesAnalyses[0].valeurMin) min.push(this.graphiques()[i].configurationSaisieDonneesAnalyses[0].valeurMin);
                if (this.graphiques()[i].configurationSaisieDonneesAnalyses[0].valeurNominal) nominal.push(this.graphiques()[i].configurationSaisieDonneesAnalyses[0].valeurNominal);
              };
              if (max.length) {
                updatedChartData[i].datasets.push({ data: max, type: "line", borderColor: "#ccc", label: "max", steppedLine: true, backgroundColor: "transparent" });
              }
              if (min.length) {
                updatedChartData[i].datasets.push({ data: min, type: "line", borderColor: "#ccc", label: "min", steppedLine: true, backgroundColor: "transparent" });
              }
              if (nominal.length) {
                updatedChartData[i].datasets.push({ data: nominal, type: "line", borderColor: "#ccc", label: "nominale", borderDash: [10, 5], backgroundColor: "transparent" });
              }
            }
            updatedCharts.push({ data: updatedChartData[i], options: updatedChartOptions[i], type: "line" });
          }
          else if (this.graphiques()[i].type == "Vibration") {


            updatedChartOptions[i] = VibrationChartOption;
            let vibrationChart = tabGraphData[i] as VibrationLineChart[];
            let datasets = [];
            for (let j = 0; j < vibrationChart.length; j++) {
              datasets[j] = {
                label: vibrationChart[j].controlPointName,
                data: vibrationChart[j].data,
                type: 'line',
                pointHitRadius: 2,
                borderwidth: 2,
                backgroundColor: "transparent",
                pointBackgroundColor: VibrationGraphColors[j],
                borderColor: VibrationGraphColors[j],
              };

            }
            updatedCharts.push({ data: { datasets: datasets }, options: updatedChartOptions[i], type: "line" });

          }
          else {
            var moys = [];
            for (var j = 0; j < tabGraphData[i].y.length; j++) {
              moys.push(tabGraphData[i].moy);
            }
            updatedChartOptions[i] = {
              maintainAspectRatio: false,
              responsive: true,
              tooltipDecimals: 2,
              legend: {
                position: 'bottom',
                labels: { usePointStyle: true }
              },
              elements: {
                point: {
                  radius: 0
                }
              }
            };

            var _label = "";
            if (this.graphiques()[i].configurationSaisieDonneesAnalyses[0]) {
              _label = await this.translate.get(this.graphiques()[i].configurationSaisieDonneesAnalyses[0].referentielDonneeAnalyse.nom).toPromise()
                .then(value => { return value })
                .catch(() => { return this.graphiques()[i].configurationSaisieDonneesAnalyses[0].referentielDonneeAnalyse.nom; });
              _label += " (" + this.graphiques()[i].configurationSaisieDonneesAnalyses[0].referentielDonneeAnalyse.nomUniteReferentiel + ")";
            }
            updatedChartData[i] = {
              // define label tree
              labels: tabGraphData[i].x,
              datasets: [
                {
                  label: _label,
                  backgroundColor: "rgba(106,0,63,0.3)",
                  borderColor: "#7E003F",
                  borderWidth: 2,
                  padding: 10,
                  itemRadius: 0,
                  data: tabGraphData[i].y
                },
                {
                  label: "Avg.",
                  backgroundColor: "transparent",
                  borderColor: "#2B729B",
                  borderWidth: 2,
                  type: "line",
                  data: moys
                }
              ]
            };
            updatedCharts.push({ data: updatedChartData[i], options: updatedChartOptions[i], type: "boxplot" });
          }
        }
        this.chartData.set(updatedChartData);
        this.chartOptions.set(updatedChartOptions);
        this.charts.set(updatedCharts);
        this.dataLoading.set(false);
        
        this.createCharts();
      });
    },
      () => {
        this.erreurRecuperation.set(true);
        this.dataLoading.set(false);
        this.createCharts();
      })
  };

  private split(parametre: string, delimiter: string, nbLoop = 1, isArray = false):string{
    let x = ''; 
    let value = ''; 
      for (let index = 1; index <= nbLoop; index++) {
        try {
            value = parametre.split(delimiter)[index].split('&')[0];

            if(isArray && value == '') continue;
            
            if(nbLoop == 1){
              x += '&' + delimiter + value;
            }else{
              
              if(value != '') {
                x += '&' + delimiter + value;
              }
            }

        } catch (error) {

          if(!isArray && nbLoop == 1){
            x += '&' + delimiter + value;
          }

        }
      }
      return x;
  }

  delete() {
    this.http.delete("Chart/" + this.graphiques()[this.indexToDelete].graphiqueId).subscribe(() => {
      if (this.chartInstances[this.indexToDelete]) {
        this.chartInstances[this.indexToDelete].destroy();
      }
      
      const updatedCharts = [...this.charts()];
      const updatedGraphiques = [...this.graphiques()];
      updatedCharts.splice(this.indexToDelete, 1);
      updatedGraphiques.splice(this.indexToDelete, 1);
      this.charts.set(updatedCharts);
      this.graphiques.set(updatedGraphiques);
      this.chartInstances.splice(this.indexToDelete, 1);
      this.modalConfirmSuppr.hide();
      
      this.createCharts();
    }, () => { this.erreurRecuperation.set(true); })
  }

  private async TraduitNom(nom: string): Promise<string> {
    let result = nom;
    if (null !== result && undefined !== result) {
      result = await this.translate.get(result).toPromise()
        .then(trad => { return trad; })
        .catch(err => { return err; });
    }

    return result;
  }

  reloadMetabase() {
    this.loadMetabase.set(false);

    setTimeout(() => {
      this.loadMetabase.set(true);
    });
  }


  ngOnDestroy() {
    this.destroyCharts();
  }

  private createCharts() {
    if (this.isCreatingCharts) {
      return;
    }
    
    this.isCreatingCharts = true;
    this.destroyCharts();
    
    setTimeout(() => {
      try {
        if (this.chartCanvases && this.chartCanvases.length > 0) {
          this.chartCanvases.forEach((canvasRef, index) => {
            if (index < this.charts().length && this.charts()[index]) {
              const chart = this.charts()[index];
              if (chart.type !== 'combinedChart' && chart.data) {
                this.createChart(canvasRef.nativeElement, chart, index);
              }
            }
          });
        }
      } catch (error) {
        console.error('Error in createCharts:', error);
      } finally {
        this.isCreatingCharts = false;
      }
    }, 200);
  }

  private createChart(canvas: HTMLCanvasElement, chartData: any, index: number) {
    if (!canvas || !chartData || !chartData.data) {
      return;
    }

    const container = canvas.parentElement;
    if (container) {
      const containerWidth = container.clientWidth || container.offsetWidth || 600;
      const containerHeight = container.clientHeight || container.offsetHeight || 300;
      
      canvas.width = containerWidth;
      canvas.height = containerHeight;
      
      canvas.style.width = '100%';
      canvas.style.height = '100%';
    }

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      try {
        const chartInstance = new Chart(ctx, {
          type: chartData.type,
          data: chartData.data,
          options: {
            ...chartData.options,
            maintainAspectRatio: false,
            responsive: true,
            responsiveAnimationDuration: 0,
            animation: {
              duration: 0
            }
          }
        });
        this.chartInstances[index] = chartInstance;
      } catch (error) {
        console.error('Error creating chart:', error);
      }
    }
  }

  private destroyCharts() {
    this.chartInstances.forEach((chart, index) => {
      if (chart && typeof chart.destroy === 'function') {
        try {
          chart.destroy();
        } catch (error) {
          console.error('Error destroying chart:', error);
        }
      }
    });
    this.chartInstances = [];

    if (this.chartCanvases) {
      this.chartCanvases.forEach(canvasRef => {
        const canvas = canvasRef.nativeElement;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      });
    }
  }
}
